"use client";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import CloseIcon from "../_icons/closeIcon";
import DeliveryImageIcon from "../_icons/deliveryImageIcon";
import DeliveryMobileImageIcon from "../_icons/deliveryMobileImageIcon";
import { EditAddressesModal } from "../editAddressesModal/editAddressesModal";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { getOrderEstimate, saveDefaultAddress } from "@/dataLayer";
import { isWithinBusinessHours } from "@/utils/restaurantCartUtils";
import { DateTime, Duration } from "luxon";

export const DeliveryOrderModal = ({
  isOpen,
  onRequestClose,
  merchant,
  merchantColor,
}: any) => {
  const [deliveryAddressModalIsOpen, setDeliveryAddressModalIsOpen] =
    useState(false);
  const [tempAddress, setTempAddress] = useState("");
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState(false);

  const handleDeliveryAddressModal = () => {
    setDeliveryAddressModalIsOpen(!deliveryAddressModalIsOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const merchantService = merchant.services.find(
    (s: any) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const leadTimeDuration = Duration.fromObject({
    minutes: merchant.leadTime,
  });
  const now = DateTime.now().setZone(merchant?.timezone);
  const currentDateTime = DateTime.fromJSDate(now.toJSDate(), {});
  const nowInMerchantZone = currentDateTime.setZone(merchant.timezone);
  const targetPickupTime = nowInMerchantZone.plus(leadTimeDuration);
  const businessHours = merchantService?.hours;
  const withinBusinessHours = isWithinBusinessHours(
    targetPickupTime,
    businessHours,
    merchant?.timezone,
    merchant?.leadTime
  );

  if (!withinBusinessHours) {
    return null;
  }

  const leftLayout = (
    <div className="flex flex-col h-full px-6 sm:px-16">
      <header className="text-center sm:text-left mt-0 sm:mt-14">
        <h2 className="text-3xl sm:text-4xl font-bold">
          You can have it delivered!
        </h2>
        <p className="mt-2 text-sm sm:text-base text-gray-700 sm:pt-4">
          This spot accepts delivery orders. Do you want to place an order for
          delivery?
        </p>
      </header>

      <div className="flex flex-col-reverse sm:flex-col items-center sm:mt-auto sm:mb-16 sm:relative absolute bottom-0 left-0 w-full">
        <button
          style={{ backgroundColor: merchantColor }}
          className="px-6 py-4 sm:px-16 text-lg font-semibold text-white sm:rounded-lg w-full sm:w-64"
          onClick={handleDeliveryAddressModal}
        >
          Order delivery
        </button>

        <button
          onClick={onRequestClose}
          style={{ color: merchantColor }}
          className="text-lg font-medium w-full border sm:border-none py-3"
          data-testid="secondary-button"
        >
          Not today
        </button>
      </div>
    </div>
  );

  if (deliveryAddressModalIsOpen) {
    return (
      <>
        <EditAddressesModal
          isOpen={deliveryAddressModalIsOpen}
          onRequestClose={onRequestClose}
          merchantColor={merchant.merchantMeta?.colorPalette?.color}
          onUpdated={async (address: any, isDefaultDelivery: boolean) => {
            setTempAddress(address);
            const result = await getOrderEstimate(merchant.id, address);
            if (result?.status?.code === 400) {
              setDeliveryAddressModalIsOpen(false);
              setDeliveryAddressErrorModalOpen(true);
            } else {
              if (isDefaultDelivery) {
                await saveDefaultAddress(address);
              }
              result &&
                localStorage.setItem("orderEstimates", JSON.stringify(result));
              address &&
                localStorage.setItem(
                  "deliveryAddress",
                  JSON.stringify(address)
                );
              window.dispatchEvent(
                new StorageEvent("storage", { key: "delivery-order-type" })
              );
            }
          }}
          checkoutCloseHandler={() => {}}
        />
        <DeliveryModalError
          onRequestClose={() => setDeliveryAddressErrorModalOpen(false)}
          merchantColor={merchantColor}
          isOpen={deliveryAddressErrorModalOpen}
          merchant={merchant}
          address={tempAddress}
          onEnterNewAddress={() => {
            setDeliveryAddressErrorModalOpen(false);
            setDeliveryAddressModalIsOpen(true);
          }}
          checkoutCloseHandler={() => {}}
        />
      </>
    );
  }

  return (
    <ReactModal
      ariaHideApp={false}
      appElement={global?.window?.document?.getElementById("app") || undefined}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="flex fixed inset-0 items-center justify-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
      shouldCloseOnOverlayClick={true}
    >
      <div className="relative bg-white sm:rounded-lg shadow-lg w-full max-w-full h-full sm:h-[580px] sm:w-[70%] mx-auto overflow-hidden">
        <button
          onClick={onRequestClose}
          className="absolute top-5 right-5 text-gray-600 hover:text-gray-900 z-10"
        >
          <CloseIcon />
        </button>
        <div className="flex flex-col sm:flex-row-reverse h-full">
          <div className="items-center justify-center sm:bg-green-100 w-full h-[300px] sm:h-full sm:w-1/2 hidden sm:flex">
            <DeliveryImageIcon brandingColor={merchantColor} />
          </div>
          <div className="items-center justify-center sm:bg-green-100 w-full h-[300px] sm:h-full sm:w-1/2 flex sm:hidden">
            <DeliveryMobileImageIcon brandingColor={merchantColor} />
          </div>
          <div className="flex flex-col w-full sm:w-1/2">{leftLayout}</div>
        </div>
      </div>
    </ReactModal>
  );
};
